// admin-analytics-expansion P1 (D1) — Thin recharts wrappers.
//
// Chart-specific dependencies (recharts) are confined to this file and its
// consumers under modules/analytics/ui/components, keeping the shared
// `@jq4race/ui` kit dependency-light (D1). `BarChart` renders distributions
// (P1); `LineChart` is included now so P4's TrendsSection can reuse the same
// primitive without duplicating axis/tooltip/legend wiring.
//
// Both charts use a fixed pixel `height` (not a percentage) so recharts can
// compute chart dimensions synchronously from props alone — this sidesteps
// jsdom's lack of real layout/ResizeObserver measurement in tests without
// any test-only branching in production code. For the same reason series set
// `isAnimationActive={false}`: recharts animates `<Line>`/`<Bar>` geometry on
// mount, and under jsdom (no rAF/coverage-instrumented timers) the animated
// path never settles, so `.recharts-line` renders empty — turning it off makes
// the final geometry render synchronously (and drops a gratuitous mount
// animation these analytics charts don't need).
//
// P4b review-fix — `LineChart` accepts an optional `yAxisAllowDecimals` +
// `valueFormatter` so a decimal/currency series (e.g. TrendsSection's net
// revenue, in dollars) can render a proper 2-decimal Y-axis + `$`-formatted
// tooltip WITHOUT this primitive hardcoding any notion of "dollars" — the
// integer volume chart (and every `BarChart` call site) is untouched and
// keeps its original whole-number axis by omitting these props.

import {
  Bar,
  CartesianGrid,
  Legend,
  Line,
  BarChart as RechartsBarChart,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';

export interface ChartSeries {
  dataKey: string;
  label?: string;
  color?: string;
}

export interface ChartPrimitiveProps {
  data: Record<string, string | number>[];
  xKey: string;
  series: ChartSeries[];
  height?: number;
}

const DEFAULT_SERIES_COLORS = ['#1d3557', '#c99a3d', '#6b8f71', '#a8553f', '#5b6b7a'];

function seriesColor(series: ChartSeries, index: number): string {
  return series.color ?? DEFAULT_SERIES_COLORS[index % DEFAULT_SERIES_COLORS.length] ?? '#1d3557';
}

export function BarChart({ data, xKey, series, height = 240 }: ChartPrimitiveProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsBarChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis allowDecimals={false} tick={{ fontSize: 11 }} width={40} />
        <Tooltip />
        {series.length > 1 && <Legend />}
        {series.map((s, i) => (
          <Bar
            key={s.dataKey}
            dataKey={s.dataKey}
            name={s.label ?? s.dataKey}
            fill={seriesColor(s, i)}
            isAnimationActive={false}
          />
        ))}
      </RechartsBarChart>
    </ResponsiveContainer>
  );
}

export interface LineChartProps extends ChartPrimitiveProps {
  /**
   * Whether the Y-axis may render decimal ticks. Defaults to `false`
   * (matches the original integer-only behavior — e.g. ticket volume).
   * Set `true` for a decimal-valued series (e.g. a dollar amount).
   */
  yAxisAllowDecimals?: boolean;
  /**
   * Formats BOTH the Y-axis ticks and the tooltip value for every series —
   * deliberately shared so the two never drift out of sync (e.g. a revenue
   * chart wants `$12.50` in both places). Defaults to recharts' own
   * plain-number rendering when omitted.
   */
  valueFormatter?: (value: number) => string;
}

export function LineChart({
  data,
  xKey,
  series,
  height = 240,
  yAxisAllowDecimals = false,
  valueFormatter,
}: LineChartProps) {
  return (
    <ResponsiveContainer width="100%" height={height}>
      <RechartsLineChart data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <CartesianGrid strokeDasharray="3 3" vertical={false} />
        <XAxis dataKey={xKey} tick={{ fontSize: 11 }} />
        <YAxis
          allowDecimals={yAxisAllowDecimals}
          tick={{ fontSize: 11 }}
          width={40}
          tickFormatter={valueFormatter}
        />
        <Tooltip
          formatter={valueFormatter ? (value) => valueFormatter(Number(value)) : undefined}
        />
        {series.length > 1 && <Legend />}
        {series.map((s, i) => (
          <Line
            key={s.dataKey}
            type="monotone"
            dataKey={s.dataKey}
            name={s.label ?? s.dataKey}
            stroke={seriesColor(s, i)}
            strokeWidth={2}
            dot={false}
            isAnimationActive={false}
          />
        ))}
      </RechartsLineChart>
    </ResponsiveContainer>
  );
}
